import { useEffect, useRef, useState } from 'react';
import { formatDateAU } from '../../../../shared/format';
import { nextCallDue } from '../../../../shared/cadence';
import type { PracticeSettings } from '../../../../shared/practiceSettings';
import type { DashboardRow } from '../../../../shared/types';
import { useAnchoredPopover } from './useAnchoredPopover';

export type NoteType = 'call' | 'meeting' | 'email' | 'text' | 'other';

/**
 * Log a call from the book in one step: the note, and Call Due rolled forward
 * by the household's tier cadence (practice settings). Unticking the roll
 * keeps the current Call Due.
 */
export function LogCallEditor({
  row, anchor, today, settings, onSave, onClose,
}: {
  row: DashboardRow;
  anchor: HTMLElement;
  today: string;
  settings: PracticeSettings;
  onSave: (input: { type: NoteType; content: string; rollCallDue: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pos = useAnchoredPopover(anchor, ref, onClose, 320);
  const [type, setType] = useState<NoteType>('call');
  const [content, setContent] = useState('');
  const [roll, setRoll] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { ref.current?.querySelector<HTMLElement>('textarea')?.focus(); }, []);

  const next = nextCallDue(row.segment, today, settings);
  const months = row.segment ? settings.callCadenceMonths[row.segment] : 3;
  const cadenceLabel = months == null ? 'no scheduled calls' : `every ${months === 1 ? 'month' : `${months} months`}`;
  const name = row.sortName ?? row.name;

  async function save() {
    setBusy(true); setErr(null);
    try {
      await onSave({ type, content: content.trim(), rollCallDue: roll });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  return (
    <div ref={ref} role="dialog" aria-label={`Log a call with ${name}`} className="qa-book-pop qa-shadow" style={{ top: pos.top, left: pos.left, width: 320 }}>
      <p className="qa-eyebrow text-[10px] mb-1">Log a call</p>
      <p className="text-sm font-medium text-ink mb-2 truncate">{name}</p>
      <div className="flex gap-1 mb-2 flex-wrap">
        {(['call', 'meeting', 'email', 'text', 'other'] as const).map((t) => (
          <button key={t} type="button" aria-pressed={type === t}
            className={`qa-book-pop__btn capitalize ${type === t ? 'is-on' : ''}`} onClick={() => setType(t)}>{t}</button>
        ))}
      </div>
      <textarea className="qa-input w-full text-sm" rows={3} aria-label="Call note" placeholder="What was discussed?"
        value={content} onChange={(e) => setContent(e.target.value)} />
      <label className="flex items-start gap-2 text-xs text-ink-soft mt-2">
        <input type="checkbox" checked={roll && next != null} disabled={next == null} onChange={(e) => setRoll(e.target.checked)} />
        <span>
          {next
            ? <>Next Call Due <span className="qa-mono text-ink">{formatDateAU(next)}</span> ({row.segment ?? 'unsegmented'}: {cadenceLabel})</>
            : <>{row.segment}: {cadenceLabel}, so Call Due stays as is.</>}
          {row.callDue && <span className="block text-ink-mute">Currently {formatDateAU(row.callDue)}</span>}
        </span>
      </label>
      <button type="button" className="btn-primary w-full text-sm mt-3" disabled={busy || !content.trim()} onClick={save}>
        Log call
      </button>
      {err && <p role="alert" className="text-xs text-rust mt-2">{err}</p>}
      <div className="flex justify-end mt-3 pt-2 border-t border-hairline-paper">
        <button type="button" className="qa-book-pop__btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
